import { z } from 'zod';
import type { ApiConfig } from './api.js';
import type { BrandAssets } from './assets.js';
import type { Auth0Config } from './auth.js';
import type { FeatureFlags } from './feature-flags.js';
import type { I18nConfig } from './i18n.js';
import type { ThemeTokens } from './theme.js';

export interface BrandConfig {
  /** Unique identifier e.g. "brinks-certificate-portal" */
  id: string;
  /** Human-readable brand display name e.g. "Brinks" */
  name: string;
  /** Production domain e.g. "portal.brinks.com" */
  domain: string;
  /** Auth0 tenant configuration (values come from env vars at build time) */
  auth?: Auth0Config;
  /** Backend API configuration */
  api: ApiConfig;
  /** Brand visual assets */
  assets: BrandAssets;
  /** Feature flag overrides for this brand */
  features: FeatureFlags;
  /**
   * Theme token documentation — CSS variable names this brand defines.
   * Actual values are in the brand app's theme.css, not here.
   */
  theme: ThemeTokens;
  /** Internationalisation configuration */
  i18n: I18nConfig;
}

// Runtime Zod schema for validating brand config at build time
export const BrandConfigSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  domain: z.string().min(1),
  auth: z
    .object({
      domain: z.string().min(1).optional(),
      clientId: z.string().min(1).optional(),
      audience: z.string().min(1).optional(),
      redirectUri: z.string().url().optional(),
      logoutReturnTo: z.string().url().optional(),
    })
    .optional()
    .default({}),
  api: z.object({
    baseUrl: z.string().url(),
    timeoutMs: z.number().positive().optional(),
  }),
  assets: z.object({
    logoUrl: z.string().min(1),
    logoInverseUrl: z.string().optional(),
    faviconUrl: z.string().min(1),
    fontFamily: z.string().min(1),
    fontFamilyMono: z.string().optional(),
  }),
  features: z.object({
    certificateDownload: z.boolean().optional(),
    bulkOperations: z.boolean().optional(),
    analyticsDashboard: z.boolean().optional(),
    userManagement: z.boolean().optional(),
  }),
  i18n: z.object({
    defaultLocale: z.string().min(1),
    supportedLocales: z.array(z.string().min(1)).min(1),
    localesPath: z.string().optional(),
  }),
});

export type BrandConfigInput = z.input<typeof BrandConfigSchema>;

/** Validate a brand config object at startup and throw if invalid */
export function validateBrandConfig(config: unknown): BrandConfig {
  return BrandConfigSchema.parse(config) as BrandConfig;
}
